import { Typography } from '@ori-ui/mui';
import { useEnvironment } from '@ori/environment';
import { useLanguage } from '@ori/i18n';
import type { Language } from '@ori/language-switcher';
import { changeLanguage } from '@ori/language-switcher';
import useTestId from '@ori/testid-generator';
import React, { type FC, useCallback } from 'react';

import { useFooter } from '../../hooks';
import { CountryInfo } from './components';
import { LANGUAGE_SELECTOR } from './constants';
import { LanguageName, Root } from './styles';

export const LanguageSelector: FC = () => {
  const { getTestId } = useTestId();
  const { tenant } = useEnvironment();
  const { language } = useLanguage();
  const { languageConfiguration, languages } = useFooter();
  const onChangeLanguage = useCallback(
    (nextLanguage: Language) => () => {
      if (nextLanguage.code !== language) {
        changeLanguage(nextLanguage, language, tenant);
      }
    },
    [language, tenant],
  );

  if (languages.length === 0 || !languageConfiguration?.countryLabel || !languageConfiguration.currentCountry) {
    return null;
  }

  return (
    <Root data-testid={getTestId(LANGUAGE_SELECTOR)}>
      {languages.length > 1 && (
        <Typography display="inline-block">
          {languageConfiguration.languageLabel}:{' '}
          {languages.map((lang, index) => (
            <React.Fragment key={lang.code || index}>
              <LanguageName
                isActive={lang.code === language}
                role="button"
                tabIndex={0}
                onClick={onChangeLanguage(lang)}
                onKeyDown={onChangeLanguage(lang)}
              >
                {lang.name}
              </LanguageName>
              {languages.length - 1 === index ? null : ' / '}
            </React.Fragment>
          ))}
        </Typography>
      )}
      <CountryInfo
        label={languageConfiguration.countryLabel}
        value={languageConfiguration.currentCountry}
      />
    </Root>
  );
};
